import type { CallbackInterface } from "recoil";

import type { PageStateType } from "Types/state";

import { loaderUpdater, pageState } from "State/page";

import { logAppError } from "Helpers/logger";

import getIntl from "Helpers/intl";

import {
  type ActivityStreamFilterType,
  type ChannelSubscriptionStatus,
  type PanelEditRecipientState,
  allEmailActivityStreamFilters,
  allSmsActivityStreamFilters,
} from "Pages/RecipientsList/model";

import { fetchDefaultConfirmationMesssage } from "Pages/RecipientsList/recoil/services/asyncSelectConfirmationMessages";
import { fetchRecipientEngagement } from "Pages/RecipientsList/recoil/services/engagement";
import { notifySubscriptionStatusUpdateSuccess } from "Pages/RecipientsList/recoil/services/notifications";
import {
  fetchRecipient,
  fetchRecipientContact,
  fetchRecipientTracking,
  fetchRecipientTrackingChangeLogs,
  getEmailRecipientTrackingInsertDate,
  panelEditRecipientUpdater,
  resultsListRecipientsUpdater,
  resultsListUpdater,
  updateRecipientField,
  updateRecipientSubscriptionStatus,
} from "Pages/RecipientsList/recoil/services/recipient";
import { getRecipientFieldValue } from "Pages/RecipientsList/recoil/services/results";
import {
  columnsState,
  dynamicFieldsState,
} from "Pages/RecipientsList/recoil/state/columns";
import { panelEditContactsState } from "Pages/RecipientsList/recoil/state/editContacts";
import { modalPendingState } from "Pages/RecipientsList/recoil/state/modalPending";
import {
  panelEditRecipientState,
  recipientActivityStreamFiltersState,
  recipientTrackingChangeLogsState,
} from "Pages/RecipientsList/recoil/state/recipient";
import { resultsState } from "Pages/RecipientsList/recoil/state/results";
import { gainsightTrack } from "mailup-react-helpers";

export const panelEditRecipientOpenCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async (id: number) => {
    //  reset(panelAddRecipientState)
    //
    const { recipient } = await snapshot.getPromise(panelEditRecipientState);

    if (recipient !== undefined) {
      set(panelEditRecipientState, (prev) => ({
        ...prev,
        recipient: undefined,
        score: undefined,
      }));
    } else {
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        loading: true,
      }));
    }

    const [columns, dynamicFields] = await Promise.all([
      snapshot.getPromise(columnsState),
      snapshot.getPromise(dynamicFieldsState),
    ]);

    try {
      const [recipient, tracking, title, trackingInsertDate] =
        await Promise.all([
          fetchRecipient(id, dynamicFields),
          fetchRecipientTracking(id),
          fetchRecipientContact(id),
          getEmailRecipientTrackingInsertDate(id),
        ]);

      const score = await fetchRecipientEngagement(id, recipient.Email);

      //const title = getRecipientFieldValue(recipient, columns[0])

      reset(panelEditContactsState);

      set(panelEditRecipientState, {
        show: true,
        title,
        recipient,
        score,
        tracking,
        trackingInsertDate: trackingInsertDate?.InsertDate,
        idNLFrom: trackingInsertDate?.IdNLFrom,
      });
    } catch (error) {
      console.error("### error", error);

      logAppError({
        Component: "RecipientsList",
        Method: "panelEditRecipientOpenCallback",
        appError: error,
      });

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: "error",
        errorMessage: getIntl().formatMessage({
          id: "Load.Alerts.ErrorGeneric",
        }),
      }));
    }

    set<PageStateType>(pageState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };

export const panelEditRecipientChangeFieldCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async (fieldId: number, fieldValue: string) => {
    const { recipient } = await snapshot.getPromise(panelEditRecipientState);
    const dynamicFields = await snapshot.getPromise(dynamicFieldsState);

    if (recipient !== undefined) {
      try {
        await updateRecipientField(
          recipient.Id,
          fieldId,
          fieldValue,
          dynamicFields,
        );

        set<PanelEditRecipientState>(
          panelEditRecipientState,
          panelEditRecipientUpdater(fieldId, fieldValue),
        );

        set(
          resultsState,
          resultsListUpdater(recipient.Id, fieldId, fieldValue),
        );
      } catch (error) {
        console.error("### error", error);

        logAppError({
          Component: "RecipientsList",
          Method: "panelEditRecipientChangeFieldCallback",
          appError: error,
        });

        set<PageStateType>(pageState, (prev) => ({
          ...prev,
          status: "error",
          errorMessage: getIntl().formatMessage({
            id: "Load.Alerts.ErrorGeneric",
          }),
        }));

        reset(panelEditRecipientState);
      }
    }
  };

export const panelEditRecipientCloseCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    gainsightTrack("mup_recipients_click_hideRightPanel", {});
    reset(panelEditRecipientState);
  };

export const panelEditRecipientChangeStatusCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async (channel: "EMAIL" | "SMS", status: ChannelSubscriptionStatus) => {
    const { recipient } = await snapshot.getPromise(panelEditRecipientState);

    if (recipient !== undefined && status !== undefined) {
      if (channel === "EMAIL" && status === "PENDING") {
        const idConfirmationMessage = await fetchDefaultConfirmationMesssage();

        set(modalPendingState, (prev) => ({
          ...prev,
          show: true,
          idRecipient: recipient.Id,
          contact: recipient.Email,
          idConfirmationMessage,
        }));

        return;
      }

      set<PageStateType>(pageState, loaderUpdater(true));

      try {
        //  change status
        await updateRecipientSubscriptionStatus(recipient.Id, channel, status);

        //  refresh recipient

        const [columns, dynamicFields] = await Promise.all([
          snapshot.getPromise(columnsState),
          snapshot.getPromise(dynamicFieldsState),
        ]);

        const updRecipient = await fetchRecipient(recipient.Id, dynamicFields);
        const title = getRecipientFieldValue(updRecipient, columns[0]);

        //  update recipient status in panel

        set(panelEditRecipientState, (prev) => ({
          ...prev,
          show: true,
          title,
          recipient: updRecipient,
        }));

        //  update recipient status in list

        set(resultsState, resultsListRecipientsUpdater(updRecipient));

        //  notify success
        notifySubscriptionStatusUpdateSuccess(status, recipient.Email);
      } catch (error) {
        console.error("### error", error);

        logAppError({
          Component: "RecipientsList",
          Method: "panelEditRecipientChangeStatusCallback",
          appError: error,
        });

        set<PageStateType>(pageState, (prev) => ({
          ...prev,
          status: "error",
          errorMessage: getIntl().formatMessage({
            id: "Load.Alerts.ErrorGeneric",
          }),
        }));

        reset(panelEditRecipientState);
      }

      set<PageStateType>(pageState, loaderUpdater(false));
    }
  };

export const reloadRecipientCallback =
  ({ snapshot, set }: CallbackInterface) =>
  async (idRecipient: number) => {
    //
    const { recipient } = await snapshot.getPromise(panelEditRecipientState);

    const [columns, dynamicFields] = await Promise.all([
      snapshot.getPromise(columnsState),
      snapshot.getPromise(dynamicFieldsState),
    ]);

    const updRecipient = await fetchRecipient(idRecipient, dynamicFields);

    if (recipient !== undefined && recipient.Id === idRecipient) {
      //  update recipient status in panel

      const title = getRecipientFieldValue(updRecipient, columns[0]);

      set(panelEditRecipientState, (prev) => ({
        ...prev,
        show: true,
        title,
        recipient: updRecipient,
      }));
    }

    //  update recipient status in list

    set(resultsState, resultsListRecipientsUpdater(updRecipient));
  };

export const initActivityStreamFiltersCallback =
  ({ set }: CallbackInterface) =>
  async (channel: "email" | "sms") => {
    set(recipientActivityStreamFiltersState, (prev) => ({
      ...prev,
      filters:
        channel === "email"
          ? allEmailActivityStreamFilters
          : allSmsActivityStreamFilters,
      allFilters: true,
    }));
  };

export const changeActivityStreamFiltersCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (v: ActivityStreamFilterType[], pAllFilters?: boolean) => {
    set(recipientActivityStreamFiltersState, (prev) => ({
      ...prev,
      filters: v,
      allFilters: pAllFilters === undefined ? prev.allFilters : pAllFilters,
    }));
  };

export const resetActivityStreamFiltersCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(recipientActivityStreamFiltersState);
  };

export const toggleActivityStreamAllFiltersCallback =
  ({ set }: CallbackInterface) =>
  async (channel: "email" | "sms") => {
    set(recipientActivityStreamFiltersState, (prev) => ({
      ...prev,
      allFilters: !prev.allFilters,
      filters:
        channel === "email"
          ? allEmailActivityStreamFilters
          : allSmsActivityStreamFilters,
    }));
  };

export const initTrackingChangeLogsCallback =
  ({ set }: CallbackInterface) =>
  async (idRecipient: number) => {
    set(recipientTrackingChangeLogsState, (prev) => ({
      ...prev,
      loading: true,
    }));

    try {
      const result = await fetchRecipientTrackingChangeLogs(idRecipient, {
        pageNumber: 1,
        pageSize: 5,
        orderBy: "InsertDate",
        orderDescending: true,
      });

      set(recipientTrackingChangeLogsState, (prev) => ({
        ...prev,
        ...result,
        status: "ready" as const,
      }));
    } catch (error) {
      console.error("### error", error);

      logAppError({
        Component: "RecipientsList",
        Method: "initTrackingChangeLogsCallback",
        appError: error as Error | undefined,
      });

      set(recipientTrackingChangeLogsState, (prev) => ({
        ...prev,
        status: "error" as const,
      }));
    }

    set(recipientTrackingChangeLogsState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };

export const fetchTrackingChangeLogsCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (
    idRecipient: number,
    pageNumber: number,
    pageSize?: number,
    orderDescending?: boolean,
  ) => {
    const prev = await snapshot.getPromise(recipientTrackingChangeLogsState);
    const newOrderDescending =
      orderDescending !== undefined ? orderDescending : prev.orderDescending;
    set(recipientTrackingChangeLogsState, (s) => ({ ...s, loading: true }));

    try {
      const result = await fetchRecipientTrackingChangeLogs(idRecipient, {
        pageNumber,
        pageSize: pageSize !== undefined ? pageSize : prev.pageSize,
        orderBy: "InsertDate",
        orderDescending: newOrderDescending,
      });

      set(recipientTrackingChangeLogsState, (s) => ({
        ...s,
        ...result,
        orderDescending: newOrderDescending,
        status: "ready" as const,
      }));
    } catch (error) {
      logAppError({
        Component: "RecipientsList",
        Method: "fetchTrackingChangeLogsCallback",
        appError: error as Error | undefined,
      });

      set(recipientTrackingChangeLogsState, (s) => ({
        ...s,
        status: "error" as const,
      }));
    }

    set(recipientTrackingChangeLogsState, (s) => ({ ...s, loading: false }));
  };

export const resetTrackingChangeLogsCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(recipientTrackingChangeLogsState);
  };
